import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  forwardRef,
  OnChanges,
  SimpleChanges,
  HostListener,
  ElementRef,
  OnDestroy
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { sortSelectOptions } from '../../utils/sort-select-options';

interface SelectOption {
  value: any;
  name: string;
}

@Component({
  selector: 'app-select-check',
  templateUrl: './select-check.component.html',
  styleUrls: ['./select-check.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectCheckComponent),
      multi: true
    }
  ]
})
export class SelectCheckComponent
  implements OnInit, OnChanges, ControlValueAccessor, OnDestroy
{
  @Input() label: string = '';
  @Input() labelColor: string = '';
  @Input() placeholder: string = 'Selecione';
  @Input() readOnly: boolean = false;
  @Input() disabled: boolean = false;
  @Input() maxLength: string | null = null;
  @Input() width: string = '';
  @Input() mask: string = '';
  @Input() requiredInput: boolean = false;
  /** Quando true, mantém a ordem original das opções. */
  @Input() preserveOrder: boolean = false;
  @Input() selectData: SelectOption[] = [];
  @Input() ngModel: any[] = [];
  @Output() ngModelChange: EventEmitter<any[]> = new EventEmitter();
  @Output() blurEvent: EventEmitter<void> = new EventEmitter();

  searchTerm: string = '';
  showOptions: boolean = false;
  filteredOptions: SelectOption[] = [];
  private orderedSelectData: SelectOption[] = [];
  private searchSubject = new Subject<string>();

  onChange: any = () => {};
  onTouched: any = () => {};

  constructor(private elementRef: ElementRef) {}

  ngOnInit(): void {
    if (this.maxLength === '') {
      this.maxLength = null;
    }
    this.applySelectDataOrder();
    this.filteredOptions = [...this.orderedSelectData];

    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(value => this.filterOptions(value));

    document.addEventListener('click', this.onClickOutside.bind(this));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectData'] || changes['preserveOrder']) {
      this.applySelectDataOrder();
      this.filterOptions(this.searchTerm);
    }
  }

  private applySelectDataOrder(): void {
    this.orderedSelectData = this.preserveOrder
      ? [...(this.selectData ?? [])]
      : sortSelectOptions(this.selectData ?? []);
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this.onClickOutside.bind(this));
  }

  get selectedOptions(): SelectOption[] {
    return this.orderedSelectData.filter(option => this.isSelected(option));
  }

  get displayPlaceholder(): string {
    const names = this.selectedOptions.map(option => option.name).join(', ');
    return names || this.placeholder;
  }

  filterOptions(searchTerm: string) {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      this.filteredOptions = this.orderedSelectData.filter(option =>
        option.name.toLowerCase().includes(term)
      );
    } else {
      this.filteredOptions = [...this.orderedSelectData];
    }
  }

  toggleSelection(option: SelectOption) {
    const current = [...(this.ngModel ?? [])];
    const index = current.findIndex(value => this.valuesMatch(value, option.value));
    if (index > -1) {
      current.splice(index, 1);
    } else {
      current.push(option.value);
    }
    this.ngModel = current;
    this.onChange(this.ngModel);
    this.ngModelChange.emit(this.ngModel);
  }

  isSelected(option: SelectOption): boolean {
    return (this.ngModel ?? []).some(value => this.valuesMatch(value, option.value));
  }

  private valuesMatch(left: unknown, right: unknown): boolean {
    if (left === right) {
      return true;
    }
    if (left == null || right == null) {
      return false;
    }
    return String(left) === String(right);
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!this.elementRef.nativeElement.contains(target)) {
      this.showOptions = false;
    }
  }

  writeValue(value: any[]): void {
    this.ngModel = Array.isArray(value) ? [...value] : [];
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.readOnly = isDisabled;
  }
}
